<template>
  <fts-modal
    width="calc(1200px + 3rem)"
    :footer="false"
    header-class="module-package-modal__header"
    content-class="module-package-modal__content"
    class="fts-module-package-modal"
    @close="close"
    v-if="showDialog"
  >
    <!-- header -->
    <template #header>
      <div class="module-package-modal-header">
        <div class="module-package-modal-header-title">
          {{ viewData.name }}
        </div>
        <div class="module-package-modal-header-description">
          <div class="module-package-key">{{ viewData.key }}</div>
          <div class="module-package-version">{{detail.version}}</div>
        </div>
      </div>
      <!-- actions -->
      <div class="module-package-modal-header-actions">
        <fts-btn
          lg
          color="secondary"
          box-shadow-none
          text-transform-init
          :loading="installLoading"
          @click="installPackage"
        >
          Install Package
        </fts-btn>
      </div>
      <fts-btn
        icon
        class="module-package-modal-close"
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </fts-btn>
    </template>
    <!-- body -->
    <template #body>
      <!-- background -->
      <div class="module-package-media">
        <div
          class="module-package-bg"
          :style="{ 'background-image': `url(${viewData.image && viewData.image.path})` }" 
          v-if="imageLoaded" />
        <div class="module-package-bg default" v-else>
          <fts-moji
              :size="120"
              :emoji="viewData.emoji" 
              class="module-package-emoji" />
            <div class="module-package-name">
              {{viewData.name}}
            </div>
        </div>
        <div class="module-package-category" v-if="viewData.category">{{viewData.category}}</div>
      </div>
      <!-- content -->
      <div class="module-package-content">
        <!-- description -->
        <div class="module-package-description-section" v-if="detail.description">
          <div class="module-package-form-legend">Description</div>
          <div class="module-package-description" v-html="detail.description"></div>
        </div>
        <!-- canvas -->
        <div class="module-package-info">
          <template v-for="card in cards">
            <div
              class="module-package-info-wrapper"         
              :key="card.id"
              v-if="detail[card.fieldName] && detail[card.fieldName].length">
              <div class="module-package-card">
                <div class="module-package-form-legend">
                  {{ card.displayName }}
                </div>
                <div class="module-package-card-content fts-scrollbar">
                  <fts-console-card-display
                    :view-data="detail[card.fieldName]"
                    :type="card.name"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </fts-modal>
</template>

<script>
import commons from '../commons/commons';

import FTSModal from "../elements/FTSModal";
import FTSBtn from "../elements/FTSBtn";
import FTSMoji from "../elements/FTSMoji";
import FTSConsoleElementDisplay from "./console/FTSConsoleElementDisplay";

export default {
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
    "fts-moji": FTSMoji,
    "fts-console-card-display": FTSConsoleElementDisplay,
  },
  data: () => ({
    showDialog: false,
    viewData: {},
    detail: {},
    resolve: null,
    imageLoaded: false,
    installLoading: false,
    cards: [
      {
        name: "information",
        fieldName: "information",
        displayName: "Information",
      },
      {
        name: "activity",
        fieldName: "activities",
        displayName: "Activity",
      },
      {
        name: "state",
        fieldName: "states",
        displayName: "State",
      },
    ],
  }),
  methods: {
    async show(item) {
      this.viewData = item;
      try {
        let res = await this.$store.dispatch(
          "loadPackageDetail",
          this.viewData.id
        );
        this.detail = res.data;

        if (this.viewData.image && this.viewData.image.path)
          commons.imageLoader(this.viewData.image.path, () => {
            this.imageLoaded = true;
          });
      } catch (err) {
        console.error(err);
      }

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.showDialog = true;
      });
    },
    close() {
      this.showDialog = false;
      this.viewData = {};
      this.detail = {};
      this.imageLoaded = false;
      this.installLoading = false;
    },
    async installPackage() {
      this.installLoading = true;
      try {
        const res = await this.$store.dispatch("installPackage", {
          key: this.viewData.key,
        });
        this.close();
        this.resolve && this.resolve(res.data);
      } catch(err) {
        if (err.response && err.response.data && err.response.data.message) {
          this.$toasted.error(err.response.data.message);
        } else {
          this.$toasted.error("unable install package");
        }
      } finally {
        this.installLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-module-package-modal {
  > .fts-modal-wrapper {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  .module-package-modal__header {
    position: relative;
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    .module-package-modal-header {
      min-width: 0;
      flex: 1;
      margin-right: 1rem;
      .module-package-modal-header-title {
        @include text-overflow-ellipsis;
        font-size: 1.5rem;
        font-weight: 600;
      }
      .module-package-modal-header-description {
        display: flex;
        padding-top: 0.375rem;
        .module-package-key {
          @include text-overflow-ellipsis;
        }
        .module-package-key,
        .module-package-version {
          color: $grayColor;
          font-size: 0.975rem;
        }
        .module-package-key + .module-package-version {
          margin-left: 1.5rem;
        }
      }
    }
    .module-package-modal-header-actions {
      @include flex-gap(
        $gap: 0.135rem,
        $width: auto,
        $display: flex,
        $wrap: nowrap
      );
    }
    .module-package-modal-close {
      position: absolute;
      top: -0.75rem;
      right: -0.75rem;
    }
  }
  .module-package-modal__content {
    padding: 0.5rem 1.5rem 1.5rem;
    .module-package-form-legend {
      margin-bottom: 0.375rem;
      font-size: 1.15rem;
      font-weight: 500;
      line-height: 1.5;
    }
    .module-package-media {
      position: relative;
      .module-package-bg {
        height: 212px;
        margin-left: -1.5rem;
        margin-right: -1.5rem;
        margin-bottom: 2rem;
        &.default {
          @include flex-center-middle;
          position: relative;    
          padding: 1rem;
          background: $primaryGradient;
          z-index: 1;
          .module-package-emoji {
            @include absolute-center-middle;
            opacity: .5;
            z-index: -1;
          }
          .module-package-name {
            @include text-line-clamp($fontSize: 2.5rem, $lineClamp: 2);
            text-align: center;
            font-weight: 600;
            color: #f5f5f5;
          }
        }
        &:not(.default) {
          background-position: center center;
          background-repeat: no-repeat;
          background-size: cover;
        }
      }
      .module-package-category {
        position: absolute;
        top: .875rem;
        left: -0.5rem;
        padding: .175rem .275rem;
        border-radius: 2px;
        font-size: .875rem;
        font-weight: 500;
        background-color: var(--fts-ternary-blue);
        color: #fff;
        text-transform: uppercase;
        z-index: 1;
      }
    }
    .module-package-content {
      max-width: 1920px;
      margin-left: auto;
      margin-right: auto;
    }
    .module-package-description-section {
      margin-bottom: 3rem;
      .module-package-description {
        color: var(--fts-gray-5);
      }
    }
    .module-package-info {
      display: flex;
      flex-wrap: wrap;
      margin-left: -1rem;
      margin-right: -1rem;
      .module-package-info-wrapper {
        .module-package-card {
          margin: 0 1rem 1rem;
          .module-package-card-content {
            border: 1px solid $borderColor;
            border-radius: 0.375rem;
          }
        }
      }
      @media (max-width: 767.98px) {
        .module-package-info-wrapper {
          flex: 1 0 100%;
          max-width: 100%;
        }
      }
      @media (min-width: 768px) and (max-width: 991.98px) {
        .module-package-info-wrapper {
          flex: 1 0 50%;
          max-width: 50%;
        }
      }
      @media (min-width: 992px) {
        .module-package-info-wrapper {
          flex: 1 0 33.333333%;
          max-width: 33.333333%;
        }
      }
    }
    .fts-console-element-display .console-element-item .console-element-content {
      padding: 3px 0.75rem ;
    }
  }
}
</style>